/**
 * docx.js — translate a Word document without touching its layout.
 *
 * WHY THIS SHAPE. Every review of document translators says the same thing:
 * DeepL, Google and ChatGPT break tables and columns. They break them because
 * they rebuild the document. A .docx does not need rebuilding — it is a zip of
 * XML, and the visible words live in <w:t> nodes. Swap those nodes and every
 * style, table, header, image and page break survives untouched, because none
 * of them was ever read.
 *
 * THE REAL DIFFICULTY — a sentence is rarely one node, and Word splits runs
 * wherever anything changes — is not solved here any more. It is solved in
 * ooxml.js, because PowerPoint splits runs exactly the same way under
 * different tag names, and two copies of one rule are how two formats start
 * translating differently without anyone noticing. This file now answers only
 * the questions that are Word's own: which parts of the zip carry words, and
 * what the finished file is called.
 *
 * Exposed as window.DTDocx.
 */
(function (root) {
    'use strict';
    if (root.DTDocx) return;

    var O = root.DTOoxml;
    var NS = O && O.W;

    /** Files inside the zip that carry visible words. */
    var TEXT_PARTS = [
        /^word\/document\.xml$/,
        /^word\/header\d*\.xml$/,
        /^word\/footer\d*\.xml$/,
        /^word\/footnotes\.xml$/,
        /^word\/endnotes\.xml$/,
        /^word\/comments\.xml$/,
    ];

    function isTextPart(name) {
        for (var i = 0; i < TEXT_PARTS.length; i++) {
            if (TEXT_PARTS[i].test(name)) return true;
        }
        return false;
    }

    /**
     * open(file, JSZip) → { units, meta, build(translated) }
     *
     * `units` is [{ id, text }] in document order — that order is what makes a
     * translation read as a document instead of a bag of strings.
     * `build` takes { id: translatedText } and returns a Blob.
     */
    async function open(file, JSZip) {
        if (!O) throw new Error('ooxml_missing');
        var zip = await JSZip.loadAsync(file);
        if (!zip.file('word/document.xml')) {
            throw new Error('not_a_docx');
        }

        var parts = {};
        var units = [];
        var names = Object.keys(zip.files).filter(isTextPart);

        for (var i = 0; i < names.length; i++) {
            var name = names[i];
            var xml = await zip.file(name).async('string');
            var r = O.unitsForPart(xml, name, NS);
            parts[name] = r.pieces;
            r.units.forEach(function (u) {
                u.id = units.length;
                units.push(u);
            });
        }

        var flattened = units.filter(function (u) { return u.flattened; }).length;

        return {
            kind: 'docx',
            units: units.map(function (u) { return { id: u.id, text: u.text }; }),
            meta: {
                parts: names,
                paragraphs: units.length,
                // Honest disclosure material: how many paragraphs will lose
                // inline emphasis because their sentence is cut by it.
                flattened: flattened,
                chars: units.reduce(function (n, u) { return n + u.text.length; }, 0),
            },

            async build(translated) {
                units.forEach(function (u) {
                    var text = translated[u.id];
                    if (text == null || text === '') return;
                    O.placeTranslation(parts[u.part], u, text);
                });

                for (var n = 0; n < names.length; n++) {
                    zip.file(names[n], O.buildPart(parts[names[n]], NS));
                }
                return zip.generateAsync({
                    type: 'blob',
                    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    compression: 'DEFLATE',
                });
            },
        };
    }

    root.DTDocx = {
        open: open,
        // exposed for tests — the behaviour lives in ooxml.js, the Word
        // namespace is bound here so the tests keep asking Word's questions.
        _splitRuns: function (xml) { return O.splitRuns(xml, NS); },
        _unitsForPart: function (xml, part) { return O.unitsForPart(xml, part, NS); },
        _escapeXml: function (s) { return O.escapeXml(s); },
        _unescapeXml: function (s) { return O.unescapeXml(s); },
    };
})(typeof self !== 'undefined' ? self : this);
